import { memo, useEffect, useState } from 'react';
// import Widget from './Doughnut';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useLocation } from 'react-router-dom';
import LightBox from 'components/ui/dialogs/LightBox';
import moment from 'moment';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

export default memo(function SalaryForStaff({ data, open, handleClose }) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { search } = useLocation();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [itemData, setItemData] = useState({});
  const [kpi, setKpi] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);

  const percent =
    (data?.report?.stats?.total_minutes /
      data?.report?.stats?.planned_minutes) *
    100;

  // object_id from /hr/alluser is the connected local user id
  const localUserId = data?.object_id;

  useEffect(() => {
    if (!open || !localUserId) return;

    // Prefer report.from / report.to from the data; fallback to URL date param
    let from = data?.report?.from;
    let till = data?.report?.to;
    if (!from || !till) {
      const params = new URLSearchParams(search);
      const dateParam = params.get('date');
      const baseMonth = dateParam
        ? moment(dateParam, 'YYYY-MM-DD')
        : data?.report?.month
        ? moment(data.report.month, 'YYYY-MM')
        : moment();
      from = baseMonth.clone().startOf('month').format('YYYY-MM-DD');
      till = baseMonth.clone().endOf('month').format('YYYY-MM-DD');
    }

    setKpiLoading(true);
    axiosPrivate
      .get(`/admin/staff-kpi/staff/${localUserId}?from=${from}&till=${till}`)
      .then((res) => setKpi(res?.data?.data || null))
      .catch(() => setKpi(null))
      .finally(() => setKpiLoading(false));
  }, [open, localUserId, data?.report?.from, data?.report?.to, search]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        maxWidth='xl'
        style={{ zIndex: 100 }}
        disableEscapeKeyDown={true}
        PaperProps={{ className: '!base-border !shadow-none' }}
      >
        <DialogTitle
          id='alert-dialog-title'
          className='!bg-gray-100 !text-center !mb-2'
        >
          <span>{data?.user_name || ''}</span>
        </DialogTitle>

        <DialogContent className='!relative'>
          <div className='h-full relative'>
            <div className='h-full mb-8'>
              {/* <div className='text-lg font-semibold mb-2 mt-4'>{office}</div> */}
              <div className='h-[calc(100%-28px)] flex gap-3 pt-2'>
                <div className='min-w-96 w-1/3'>
                  {/* <Widget
                    data={{ ...data, date: data?.report?.month }}
                    stats={data?.report?.stats}
                  /> */}
                  <div className='w-full flex items-center justify-center p-4 mb-2'>
                    {data?.report?.image ? (
                      <img
                        src={`${data?.report?.image}`}
                        className='w-24 h-24 object-cover transition-all rounded-full cursor-pointer hover:brightness-50'
                        alt=''
                        onClick={() => {
                          setOpenImageModal(true);
                          setItemData({
                            image: data?.report?.image,
                            name: data?.user_name,
                            time: 'profile',
                          });
                        }}
                      />
                    ) : (
                      <div>
                        <i className='bi bi-no-image' />
                      </div>
                    )}
                  </div>
                  <div className='w-full grid grid-cols-2 gap-2'>
                    {data?.report?.stats
                      ? Object?.entries?.(data?.report?.stats)?.map(
                          ([name, value]) => (
                            <div key={name} className='max-h-20 mb-2'>
                              <div className='bg-slate-50 p-4 rounded-md'>
                                <div className='font-bold text-gray-500'>
                                  {t(`boss.offices.workStatuses.${name}`)}
                                </div>
                                <div className='text-[20px] font-bold'>
                                  {value ? Number(Number(value).toFixed(2)) : 0}{' '}
                                  {t('common.fields.mins')}
                                </div>
                              </div>
                            </div>
                          ),
                        )
                      : ''}
                  </div>
                  <div className='my-10'>
                    <div className='w-full flex gap-1 items-center'>
                      <div>0</div>
                      <div className='bg-blue-100 h-3 grow rounded-full'>
                        <div
                          style={{ width: `${percent}%` }}
                          className='bg-blue-600 h-3 rounded-full'
                        />
                      </div>
                      <div className='whitespace-nowrap flex flex-col items-end leading-tight'>
                        <span className='text-green-600 font-semibold'>
                          <CurrencyFormat
                            value={Number(
                              (percent * data?.report?.salary) / 100,
                            )}
                            suffix={` ${data?.report?.valute}`}
                            decimalScale={2}
                          />
                        </span>
                        <span className='text-gray-500 text-xs'>
                          /
                          <CurrencyFormat
                            value={data?.report?.salary}
                            suffix={` ${data?.report?.valute}`}
                            decimalScale={2}
                          />
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* KPI section */}
                  <div className='mt-4'>
                    <div className='flex items-center justify-between mb-2'>
                      <span className='font-bold text-gray-700'>
                        <i className='bi bi-cash-coin mr-1' />
                        {t('settings.staffKpi.earnings.title')}
                      </span>
                      {kpiLoading && <CircularProgress size={14} />}
                    </div>

                    {!kpiLoading && kpi?.items?.length > 0 && (
                      <div className='flex flex-col gap-1.5'>
                        {kpi.items.map((item, idx) => (
                          <div
                            key={idx}
                            className='flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 bg-white'
                          >
                            <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
                              <span className='font-medium text-xs text-gray-800 truncate'>
                                {item?.kpi?.type?.name || `#${item?.kpi?.type_id}`}
                              </span>
                              <span className='text-[11px] text-gray-500 flex items-center gap-1'>
                                {item?.kpi?.count_type_text} ·
                                <NumericFormat
                                  value={item?.kpi?.price}
                                  displayType='text'
                                  thousandSeparator={item?.kpi?.count_type === 2 ? ' ' : ''}
                                  decimalScale={2}
                                />
                                {item?.kpi?.count_type === 1 ? '%' : ''}
                              </span>
                            </div>
                            <div className='flex flex-col items-end text-xs font-medium shrink-0'>
                              {item?.earned_sum > 0 && (
                                <NumericFormat
                                  value={item.earned_sum}
                                  displayType='text'
                                  thousandSeparator=' '
                                  decimalScale={2}
                                  suffix=' UZS'
                                />
                              )}
                              {item?.earned_valute > 0 && (
                                <NumericFormat
                                  value={item.earned_valute}
                                  displayType='text'
                                  thousandSeparator=' '
                                  decimalScale={2}
                                  suffix=' $'
                                />
                              )}
                              {!item?.earned_sum && !item?.earned_valute && (
                                <span className='text-gray-400'>—</span>
                              )}
                            </div>
                          </div>
                        ))}
                        <div className='flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mt-1'>
                          <span className='font-medium text-emerald-700 text-xs'>
                            {t('common.table.total')}:
                          </span>
                          <div className='flex flex-col items-end font-semibold text-emerald-700 text-xs'>
                            {kpi?.total_sum > 0 && (
                              <NumericFormat
                                value={kpi.total_sum}
                                displayType='text'
                                thousandSeparator=' '
                                decimalScale={2}
                                suffix=' UZS'
                              />
                            )}
                            {kpi?.total_valute > 0 && (
                              <NumericFormat
                                value={kpi.total_valute}
                                displayType='text'
                                thousandSeparator=' '
                                decimalScale={2}
                                suffix=' $'
                              />
                            )}
                            {!kpi?.total_sum && !kpi?.total_valute && (
                              <span>0 UZS</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {!localUserId && (
                      <div className='rounded-lg bg-amber-50 border border-amber-200 px-3 py-3 text-center text-amber-700 text-xs'>
                        <i className='bi bi-exclamation-triangle mr-1' />
                        {t('hr.connect.notConnected')}
                      </div>
                    )}

                    {localUserId && !kpiLoading && (!kpi?.items || kpi?.items?.length === 0) && (
                      <div className='rounded-lg bg-gray-50 px-3 py-3 text-center text-gray-400 text-xs'>
                        <i className='bi bi-exclamation-octagon mr-1' />
                        {t('common.global.noDataFound')}
                      </div>
                    )}
                  </div>
                </div>
                <div className='col-span-2 flex-grow'>
                  <TableContainer className='!base-border !shadow-none !h-full'>
                    <Table className='!bg-white' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell className='!bg-gray-50'>#</TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('common.fields.date')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('boss.offices.workStatuses.planned_minutes')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('boss.offices.workStatuses.total_minutes')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('boss.offices.workStatuses.under_minutes')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('common.table.scheduleTime')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('common.table.checkIn')}
                          </TableCell>
                          <TableCell className='!bg-gray-50'>
                            {t('common.table.checkOut')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody className='overflow-hidden'>
                        {data?.report?.list?.map((row, index) => {
                          const time1 = moment(
                            `${row?.date} ${row?.schedule?.start}`,
                            'YYYY-MM-DD HH:mm',
                          );
                          const time2 = moment(
                            `${row?.date} ${row?.attendance?.start}`,
                            'YYYY-MM-DD HH:mm',
                          );
                          const time3 = moment(
                            `${row?.date} ${row?.schedule?.end}`,
                            'YYYY-MM-DD HH:mm',
                          );
                          const time4 = moment(
                            `${row?.date} ${row?.attendance?.end}`,
                            'YYYY-MM-DD HH:mm',
                          );

                          const isAfter = !time1.isBefore(time2);

                          const isBefore = !time3.isAfter(time4);

                          return (
                            <TableRow
                              key={row.id}
                              hover
                              className='cursor-pointer'
                            >
                              <TableCell size='small'>
                                <div className='min-h-[28px] h-full flex items-center'>
                                  {index + 1}
                                </div>
                              </TableCell>
                              <TableCell size='small'>
                                {moment(row?.date, 'YYYY-MM-DD').format(
                                  'DD.MM.YYYY',
                                )}
                              </TableCell>
                              <TableCell size='small'>
                                {row?.planned
                                  ? Number(Number(row?.planned)?.toFixed(2))
                                  : '0'}
                              </TableCell>
                              <TableCell size='small'>
                                {row?.worked
                                  ? Number(Number(row?.worked)?.toFixed(2))
                                  : '0'}
                              </TableCell>
                              <TableCell size='small'>
                                {row?.diff
                                  ? Number(Number(row?.diff)?.toFixed(2))
                                  : '0'}
                              </TableCell>
                              <TableCell
                                size='small'
                                className='!whitespace-nowrap'
                              >
                                {row?.day_of ? (
                                  <span className='font-bold'>
                                    {t('boss.offices.statuses.day_off')}
                                  </span>
                                ) : (
                                  `${row?.schedule?.start}-${row?.schedule?.end}`
                                )}
                              </TableCell>
                              <TableCell
                                size='small'
                                className='!whitespace-nowrap'
                              >
                                <div className='flex items-center justify-between'>
                                  <span
                                    className='mr-2'
                                    style={{
                                      color: isAfter ? 'green' : 'red',
                                    }}
                                  >
                                    {row?.attendance?.start}
                                  </span>

                                  {row?.attendance?.start_image ? (
                                    <Button
                                      size='small'
                                      color='info'
                                      variant='contained'
                                      className='!max-w-[45px]'
                                      onClick={() => {
                                        setOpenImageModal(true);
                                        setItemData({
                                          image: row?.attendance?.start_image,
                                          name: row?.user,
                                          time: row?.attendance?.start,
                                        });
                                      }}
                                    >
                                      <i className='bi bi-person-bounding-box' />
                                    </Button>
                                  ) : (
                                    ''
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                size='small'
                                className='!whitespace-nowrap'
                              >
                                <div className='flex items-center justify-between'>
                                  <span
                                    className='mr-2'
                                    style={{
                                      color: isBefore ? 'green' : 'red',
                                    }}
                                  >
                                    {row?.attendance?.end}
                                  </span>

                                  {row?.attendance?.end_image ? (
                                    <Button
                                      size='small'
                                      color='info'
                                      variant='contained'
                                      className='!max-w-[45px]'
                                      onClick={() => {
                                        setOpenImageModal(true);
                                        setItemData({
                                          image: row?.attendance?.end_image,
                                          name: row?.user,
                                          time: row?.attendance?.end,
                                        });
                                      }}
                                    >
                                      <i className='bi bi-person-bounding-box' />
                                    </Button>
                                  ) : (
                                    ''
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {openImageModal && itemData?.image ? (
        // yet-another-react-lightbox
        <LightBox
          isOpen={openImageModal}
          style={{ zIndex: 9999999 }}
          captions={[`${itemData?.name} - ${itemData?.time}`]}
          titles={[`${itemData?.name} - ${itemData?.time}`]}
          images={[`${itemData?.image}`]}
          photoIndex={0}
          setIsOpen={() => {
            setOpenImageModal(false);
            setItemData({});
          }}
        />
      ) : null}
    </>
  );
});
